import { useState, useRef, useCallback, useEffect, ReactNode } from 'react';
import './expandable-menu.scss';

export interface ExpandableMenuItem {
  id: string;
  content: ReactNode;
}

interface ExpandableMenuProps {
  // Main content that's always visible
  mainContent: ReactNode;

  // Additional items to show in the popup
  additionalItems: ExpandableMenuItem[];

  // Custom trigger component (defaults to "+X" format)
  triggerComponent?: (
    count: number,
    onToggle: (e: React.MouseEvent) => void
  ) => ReactNode;

  // Custom render function for popup items
  renderPopupItem?: (item: ExpandableMenuItem, index: number) => ReactNode;

  // Whether to show the component at all
  isVisible?: boolean;

  // Custom CSS classes
  className?: string;
  triggerClassName?: string;
  popupClassName?: string;
  popupItemClassName?: string;

  // Unique key for forcing re-renders (like productVariantId in original)
  uniqueKey?: string | number;
}

export const ExpandableMenu: React.FC<ExpandableMenuProps> = ({
  mainContent,
  additionalItems = [],
  triggerComponent,
  renderPopupItem,
  isVisible = true,
  className = '',
  triggerClassName = '',
  popupClassName = '',
  popupItemClassName = '',
  uniqueKey,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  }, []);

  // Click anywhere outside popup to close it (mobile-compatible)
  useEffect(() => {
    if (!isExpanded) return;

    const handleOutsideInteraction = (event: Event) => {
      const target = event.target as Node;
      const isOutsidePopup =
        popupRef.current && !popupRef.current.contains(target);
      const isOutsideTrigger =
        triggerRef.current && !triggerRef.current.contains(target);

      // Close popup if interaction is outside both popup and trigger
      if (isOutsidePopup && isOutsideTrigger) {
        setIsExpanded(false);
      }
    };

    // For mobile: handle both touch and click events
    // touchstart fires before click on mobile and provides better UX
    document.addEventListener('touchstart', handleOutsideInteraction, {
      passive: true,
    });
    document.addEventListener('mousedown', handleOutsideInteraction);

    // Optional: Close on scroll for mobile (prevents awkward floating popups)
    const handleScroll = () => {
      setIsExpanded(false);
    };

    // Add scroll listener for mobile
    document.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup all event listeners
    return () => {
      document.removeEventListener('touchstart', handleOutsideInteraction);
      document.removeEventListener('mousedown', handleOutsideInteraction);
      document.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isExpanded]);

  // Reset expansion state when uniqueKey changes
  useEffect(() => {
    setIsExpanded(false);
  }, [uniqueKey]);

  if (!isVisible || !mainContent) return null;

  const hasAdditionalItems = additionalItems.length > 0;

  // Default trigger component
  const defaultTriggerComponent = (
    count: number,
    onToggle: (e: React.MouseEvent) => void
  ) => (
    <div
      ref={triggerRef}
      className={`trigger-wrapper ${triggerClassName}`}
      onClick={onToggle}
    >
      <span className="extra-btn font-semibold">+{count}</span>
    </div>
  );

  // Default popup item renderer
  const defaultRenderPopupItem = (item: ExpandableMenuItem, index: number) => (
    <div className={`popup-item ${popupItemClassName}`} key={item.id}>
      {item.content}
    </div>
  );

  const renderTrigger = triggerComponent || defaultTriggerComponent;
  const renderItem = renderPopupItem || defaultRenderPopupItem;

  return (
    <div className={`expandable-menu ${className}`}>
      {mainContent}

      {hasAdditionalItems && (
        <>
          {/* <span className="separator font-semibold">•</span> */}
          <div className="menu-wrapper">
            <div ref={triggerRef}>
              {renderTrigger(additionalItems.length, handleToggle)}
            </div>
            {isExpanded && (
              <div
                className={`popup ${popupClassName}`}
                ref={popupRef}
                key={uniqueKey}
              >
                {additionalItems.map((item, index) => renderItem(item, index))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
